import { useState, useEffect, useCallback } from 'react'
import { message } from 'antd'
import { countInvoices, generateCraftMyPdfReport, getInvoices, invoicesAggregate, updateInvoice } from '../../../utils'
import { readPatients } from '../../../../Admin/patients/utils/utils'
import { handleError } from '../../../../lib/utils'
import { goodXStatus } from '../../../../Bookings/utils/utils'
import dayjs from 'dayjs'
import InvoiceTemplate from '../components/InvoiceTemplate'
import store from '../../../../../store'
import { useSelector } from 'react-redux'
import {
  logAuditEvent,
  AuditActions,
  AuditEntityTypes
} from '../../../../Billing-Module-Enhanced/backend-integration/api/auditTrail'

/**
 * Custom hook for managing invoices list operations
 */
export const useInvoices = () => {
  const [loading, setLoading] = useState(false)
  const [data, setData] = useState([])
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [total, setTotal] = useState(0)
  const [filters, setFilters] = useState({
    status: undefined,
    invoiceType: undefined,
    createdAt: undefined,
    search: undefined
  })
  const [printingInvoiceId, setPrintingInvoiceId] = useState(null)

  /**
   * Fetches invoices based on current filters and pagination
   */
  const fetchInvoices = async () => {
    let tmpFilters = { ...filters }

    setLoading(true)

    try {
      // Process filters more efficiently
      Object.keys(tmpFilters).forEach((key) => {
        if (!tmpFilters[key]) {
          delete tmpFilters[key]
          return
        }

        if (key === 'createdAt') {
          const date = new Date(tmpFilters[key])
          tmpFilters[key] = {
            $lte: dayjs(date).set('hour', 23).set('minute', 59).set('second', 59).toDate(),
            $gte: dayjs(date).set('hour', 0).set('minute', 0).set('second', 0).toDate()
          }
        }

        if (key === 'search') {
          tmpFilters.$or = [
            { invoiceNo: { $regex: tmpFilters[key], $options: 'i' } },
            { claimStatus: { $regex: tmpFilters[key], $options: 'i' } },
            { status: { $regex: tmpFilters[key], $options: 'i' } }
          ]
          delete tmpFilters[key]
        }
      })

      // Get total count and invoice data in parallel
      const [tmpTotal, tmpData] = await Promise.all([
        countInvoices(tmpFilters),
        getInvoices(tmpFilters, page, pageSize)
      ])

      // Extract the count value from the response
      const totalCount = typeof tmpTotal === 'object' ? tmpTotal.result || tmpTotal.count || 0 : tmpTotal
      setTotal(totalCount)

      // Collect all IDs in a single pass
      const patientIds = new Set()
      const bookingIds = new Set()
      const clinicIds = new Set()
      const medicalProfIds = new Set()
      const goodXTicketNos = []

      tmpData.forEach((entry) => {
        patientIds.add(entry.userRef)

        if (entry.medicalProfRef) medicalProfIds.add(entry.medicalProfRef)
        if (entry.refMedProfRef) medicalProfIds.add(entry.refMedProfRef)
        if (entry.assMedProfRef) medicalProfIds.add(entry.assMedProfRef)

        if (entry.bookingRef) bookingIds.add(entry.bookingRef)
        if (entry.clinicRef) clinicIds.add(entry.clinicRef)

        if (entry.goodXTicketNo)
          goodXTicketNos.push({
            index: tmpData.indexOf(entry),
            ticketNo: entry.goodXTicketNo
          })
      })

      // Process GoodX data in parallel if needed
      if (goodXTicketNos.length > 0) {
        // Create all promises at once
        const statusPromises = goodXTicketNos.map(async ({ index, ticketNo }) => {
          const status = await goodXStatus(ticketNo)
          return {
            index,
            goodXStatus: status,
            claimResponse: null,
            claimStatus: 'N/A'
          }
        })

        // Execute all promises in parallel
        const results = await Promise.all(statusPromises)

        // Apply GoodX results to tmpData
        results.forEach((result) => {
          Object.assign(tmpData[result.index], {
            goodXStatus: result.goodXStatus,
            claimResponse: result.claimResponse,
            claimStatus: result.claimStatus
          })
        })
      }

      // Set default GoodX values for entries without goodXTicketNo
      tmpData.forEach((entry) => {
        if (!entry.goodXTicketNo) {
          entry.goodXStatus = 'Private'
          entry.claimResponse = null
          entry.claimStatus = 'N/A'
        }
      })

      // Fetch patient and booking data in parallel
      const [patientRecords] = await Promise.all([
        readPatients({ _id: { $in: Array.from(patientIds) } }, {}, 'firstName lastName idNo')
      ])

      // Create lookup maps for faster access
      const patientMap = new Map(patientRecords.map((p) => [p._id, p]))

      // Apply patient and booking data in a single pass
      tmpData.forEach((entry) => {
        const patient = patientMap.get(entry.userRef)
        if (patient) {
          entry.fullName = `${patient.firstName} ${patient.lastName}`
          entry.idNo = patient.idNo
        }
      })

      setData([...tmpData])
    } catch (e) {
      message.error(handleError(e, true))
    }

    setLoading(false)
  }

  /**
   * Marks invoice as complete
   */
  const markInvoiceAsComplete = async (invoiceId) => {
    try {
      // First get the invoice data to extract billingId
      const invoice = data.find((inv) => inv._id === invoiceId)

      await updateInvoice(invoiceId, { status: 'completed' })

      // Log audit trail for invoice completion
      await logAuditEvent(AuditActions.UPDATE, AuditEntityTypes.INVOICE, invoiceId, 'Invoice marked as completed', {
        invoiceId,
        billingId: invoice?.billingId,
        newStatus: 'completed',
        action: 'status_update'
      })

      message.success('Invoice marked as completed')
      fetchInvoices()
      return true
    } catch (e) {
      message.error(handleError(e, true))
      return false
    }
  }

  const handlePrintInvoice = async (record) => {
    setPrintingInvoiceId(record._id)
    try {
      const entity = store.getState().core.entity
      const invoice = await invoicesAggregate([
        {
          $match: {
            $expr: { $eq: [{ $toString: '$_id' }, record._id] }
          }
        },
        {
          $addFields: {
            userRefObjectId: { $cond: [{ $eq: ['$userRef', null] }, null, { $toObjectId: '$userRef' }] },
            clinicRefObjectId: { $cond: [{ $eq: ['$clinicRef', null] }, null, { $toObjectId: '$clinicRef' }] },
            medicalProfRefObjectId: {
              $cond: [{ $eq: ['$medicalProfRef', null] }, null, { $toObjectId: '$medicalProfRef' }]
            }
          }
        },
        {
          $lookup: {
            from: `${process.env.REACT_APP_AGILITE_TEAM_ID}_admin_clinics`,
            localField: 'clinicRefObjectId',
            foreignField: '_id',
            as: 'clinicRecord'
          }
        },
        {
          $lookup: {
            from: `${process.env.REACT_APP_AGILITE_TEAM_ID}_auth_users`,
            localField: 'userRefObjectId',
            foreignField: '_id',
            as: 'patientRecord'
          }
        },
        {
          $lookup: {
            from: `${process.env.REACT_APP_AGILITE_TEAM_ID}_auth_users`,
            localField: 'medicalProfRefObjectId',
            foreignField: '_id',
            as: 'medicalProfRecord'
          }
        },
        {
          $lookup: {
            from: `${process.env.REACT_APP_AGILITE_TEAM_ID}_billing_records`,
            localField: 'bookingRef',
            foreignField: 'bookingRef',
            as: 'billingRecords'
          }
        },
        {
          $project: {
            patientRecord: {
              profileImage: 0
            },
            medicalProfRecord: {
              profileImage: 0
            },
            clinicRecord: {
              clinicLogo: 0,
              billingMacros: 0
            }
          }
        },
        {
          $unwind: '$clinicRecord'
        },
        {
          $unwind: '$patientRecord'
        },
        {
          $unwind: '$medicalProfRecord'
        }
      ])

      const invoiceHtml = InvoiceTemplate({ ...invoice[0], invoice: record, entity })

      const pdfUrl = await generateCraftMyPdfReport(invoiceHtml, 'b1b77b233fd40c3c')
      window.open(pdfUrl, '_blank')
    } catch (error) {
      console.error('Error printing invoice:', error)
      message.error('Failed to generate invoice PDF')
    } finally {
      setPrintingInvoiceId(null)
    }
  }

  /**
   * Manually check GoodX status for a single invoice
   */
  const checkInvoiceStatus = async (invoice) => {
    // Check if invoice has a GoodX ticket number (both medical aid and compensation claims can have this)
    if (!invoice.goodXTicketNo) {
      const claimType = invoice.claimType || 'medical_aid'
      if (['COID', 'FEM', 'SAPS', 'RMA'].includes(claimType)) {
        message.warning(`This ${claimType} compensation claim was not submitted to GoodX`)
      } else {
        message.warning('This invoice was not submitted to medical aid')
      }
      return false
    }

    try {
      // Show loading message
      const hide = message.loading(`Checking status for invoice ${invoice.invoiceNo}...`, 0)

      console.log(`🔄 [INVOICE-STATUS] Checking status for invoice ${invoice.invoiceNo}`)
      console.log('🔄 [INVOICE-STATUS] Invoice details:', {
        invoiceNo: invoice.invoiceNo,
        goodXTicketNo: invoice.goodXTicketNo,
        claimType: invoice.claimType,
        currentStatus: invoice.goodXStatus,
        invoiceType: invoice.invoiceType
      })

      // The goodXStatus function returns the claim status (mfACK, mfAPP, etc.)
      let claimStatus = await goodXStatus(invoice.goodXTicketNo)

      console.log(`🔄 [INVOICE-STATUS] Status check result for ${invoice.invoiceNo}:`, claimStatus)

      let ticketStatus = 'Processing' // Default ticket status
      let additionalInfo = {}
      let responseMessage = `Status updated: ${claimStatus}`

      // Test: Try to get detailed response for both medical aid AND IOD claims when status is Processed_successful
      const isIODClaim = ['COID', 'FEM', 'SAPS', 'RMA'].includes(invoice.claimType)
      const shouldTryDetailedResponse = !isIODClaim || claimStatus === 'Processed_successful'

      if (shouldTryDetailedResponse) {
        try {
          const claimTypeDesc = isIODClaim ? `IOD claim (${invoice.claimType})` : 'medical aid claim'
          console.log(
            `🔍 [INVOICE-STATUS] Attempting detailed response for ${claimTypeDesc} with status: ${claimStatus}`
          )
          const { goodXResponse } = await import('../../../../Bookings/utils/utils')
          const fullResponse = await goodXResponse(invoice.goodXTicketNo)

          // Map claim status to ticket status
          if (claimStatus === 'mfAPP' || claimStatus === 'PAID') {
            ticketStatus = 'Processed_successful'
          } else if (claimStatus === 'mfREJ' || claimStatus === 'swREJ') {
            ticketStatus = 'Failed'
          } else if (claimStatus === 'mfACK' || claimStatus === 'swACC' || claimStatus === 'Entry_created') {
            ticketStatus = 'Processing'
          } else if (claimStatus === 'Processed_successful') {
            // Sometimes the API returns the ticket status instead of claim status
            ticketStatus = claimStatus
            // Try to get the real claim status from the response
            if (fullResponse?.ProviderClaimResponse?.CommonProviderClaimResponseInfo?.[0]) {
              const claimInfo = fullResponse.ProviderClaimResponse.CommonProviderClaimResponseInfo[0]
              const responseClaimStatus = claimInfo.ClaimHeaderStatusCode?.[0]
              if (responseClaimStatus) {
                claimStatus = responseClaimStatus
                responseMessage = `Status updated: ${responseClaimStatus}`
              }
            }
          } else {
            ticketStatus = 'Pending'
          }

          // Log both statuses for debugging
          console.log('GoodX Status Check:', {
            claimStatus: claimStatus,
            ticketStatus: ticketStatus
          })

          // Extract financial information if available
          if (fullResponse?.ProviderClaimResponse?.CommonProviderClaimResponseInfo?.[0]) {
            const claimInfo = fullResponse.ProviderClaimResponse.CommonProviderClaimResponseInfo[0]

            if (claimInfo.ClaimHeaderFinancial?.[0]?.CommonClaimBaseFinancialInfo?.[0]) {
              const financial = claimInfo.ClaimHeaderFinancial[0].CommonClaimBaseFinancialInfo[0]
              additionalInfo.approvedAmount = parseFloat(financial.ApprovedClaimAmount?.[0] || 0)
              additionalInfo.paidAmount = parseFloat(financial.PaidClaimAmount?.[0] || 0)

              if (additionalInfo.approvedAmount > 0) {
                responseMessage += ` - Approved: R${additionalInfo.approvedAmount.toFixed(2)}`
              }
            }
          }
        } catch (err) {
          const claimType = invoice.claimType || 'medical aid'
          console.warn(
            `⚠️ [INVOICE-STATUS] Could not fetch detailed response for ${claimType} claim ${invoice.invoiceNo}:`,
            {
              error: err.message,
              status: err.response?.status,
              claimType: claimType,
              ticketNo: invoice.goodXTicketNo
            }
          )

          // For compensation claims, this might be expected if Node-RED doesn't support detailed responses for IOD claims
          if (['COID', 'FEM', 'SAPS', 'RMA'].includes(claimType)) {
            if (claimStatus === 'Processed_successful') {
              console.log(
                `⚠️ [INVOICE-STATUS] ${claimType} claim with Processed_successful status still has no detailed response - GoodX may not support this`
              )
            } else {
              console.log(
                `ℹ️ [INVOICE-STATUS] ${claimType} claim with status ${claimStatus} - detailed response may only be available when Processed_successful`
              )
            }
          }

          // If we can't get the full response, map claim status to ticket status
          if (claimStatus === 'mfAPP' || claimStatus === 'PAID') {
            ticketStatus = 'Processed_successful'
          } else if (claimStatus === 'mfREJ' || claimStatus === 'swREJ') {
            ticketStatus = 'Failed'
          } else if (claimStatus === 'mfACK' || claimStatus === 'swACC') {
            ticketStatus = 'Processing'
          }
        }
      } else {
        // For IOD claims, only use basic status mapping (no detailed responses)
        console.log(`ℹ️ [INVOICE-STATUS] IOD claim (${invoice.claimType}) - using basic status mapping only`)
        if (claimStatus === 'mfAPP' || claimStatus === 'PAID') {
          ticketStatus = 'Processed_successful'
        } else if (claimStatus === 'mfREJ' || claimStatus === 'swREJ') {
          ticketStatus = 'Failed'
        } else if (claimStatus === 'mfACK' || claimStatus === 'swACC' || claimStatus === 'Entry_created') {
          ticketStatus = 'Processing'
        } else if (claimStatus === 'Processed_successful') {
          ticketStatus = claimStatus
        } else {
          ticketStatus = 'Pending'
        }
      }

      // Update invoice with both statuses correctly
      await updateInvoice(invoice._id, {
        goodXStatus: claimStatus, // The claim status (mfACK, mfAPP, etc.)
        goodXTicketStatus: ticketStatus, // The ticket status (Processed_successful, Failed, etc.)
        lastStatusCheck: new Date(),
        ...additionalInfo
      })

      // Also update the corresponding ledger transaction
      try {
        // Find the ledger transaction for this invoice
        const { default: Agilite } = await import('agilite')
        const { default: BillingEnums } = await import('../../../../Billing-Module-Enhanced/utils/enums')

        // Create Agilite instance
        const agilite = new Agilite({
          apiServerUrl: process.env.REACT_APP_AGILITE_API_URL,
          apiKey: process.env.REACT_APP_AGILITE_API_KEY
        })

        console.log('Looking for ledger transaction with:', {
          invoiceRef: invoice._id,
          invoiceNo: invoice.invoiceNo,
          type: 'invoice'
        })

        // Try searching by invoiceNo first, then by invoiceRef as fallback
        let ledgerResponse = await agilite.Connectors.execute(
          BillingEnums.profileKeys.PATIENT_LEDGER,
          BillingEnums.routeKeys.FIND_ONE,
          {
            data: JSON.stringify({
              invoiceNo: invoice.invoiceNo,
              type: 'invoice'
            })
          }
        )

        // If not found by invoiceNo, try invoiceRef
        if (!ledgerResponse?.data?._id) {
          ledgerResponse = await agilite.Connectors.execute(
            BillingEnums.profileKeys.PATIENT_LEDGER,
            BillingEnums.routeKeys.FIND_ONE,
            {
              data: JSON.stringify({
                invoiceRef: invoice._id,
                type: 'invoice'
              })
            }
          )
        }

        console.log('Ledger search response:', ledgerResponse)

        if (ledgerResponse?.data?._id) {
          // Update the ledger transaction with the actual claim status
          const updateResult = await agilite.Connectors.execute(
            BillingEnums.profileKeys.PATIENT_LEDGER,
            BillingEnums.routeKeys.UPDATE,
            {
              recordId: ledgerResponse.data._id,
              data: JSON.stringify({
                status: claimStatus,
                goodXStatus: claimStatus,
                goodXTicketStatus: ticketStatus,
                lastStatusCheck: new Date()
              })
            }
          )
          console.log(
            `Updated ledger transaction ${ledgerResponse.data._id} with claim status: ${claimStatus}, ticket status: ${ticketStatus}`,
            updateResult
          )
        } else {
          console.warn('No ledger transaction found for invoice:', {
            invoiceId: invoice._id,
            invoiceNo: invoice.invoiceNo
          })
        }
      } catch (err) {
        console.error('Failed to update ledger transaction:', err)
        // Don't fail the whole operation if ledger update fails
      }

      hide()
      message.success(responseMessage)

      // Log audit trail
      await logAuditEvent(AuditActions.UPDATE, AuditEntityTypes.INVOICE, invoice._id, 'Manual GoodX status check', {
        invoiceId: invoice._id,
        billingId: invoice.billingId,
        oldStatus: invoice.goodXStatus,
        newStatus: claimStatus,
        ticketStatus: ticketStatus,
        ticketNo: invoice.goodXTicketNo
      })

      // Refresh the list to show updated status
      await fetchInvoices()

      // If claim is approved or paid, check for payment updates
      if (claimStatus === 'mfAPP' || claimStatus === 'PAID' || ticketStatus === 'Processed_successful') {
        try {
          const { syncMedicalAidPayments } = await import(
            '../../../../Billing-Module-Enhanced/backend-integration/api/billingIntegration'
          )
          await syncMedicalAidPayments(invoice.userRef)
        } catch (err) {
          console.error('Failed to sync medical aid payments:', err)
        }
      }

      return true
    } catch (error) {
      message.error(`Failed to check status: ${error.message || 'Unknown error'}`)
      return false
    }
  }

  /**
   * Bulk check GoodX status for all pending invoices
   */
  const checkAllPendingStatuses = async () => {
    // Filter invoices that have goodXTicketNo and are not in final states
    // Include both medical aid and compensation fund claims with GoodX tickets
    const pendingInvoices = data.filter(
      (invoice) =>
        invoice.goodXTicketNo &&
        invoice.goodXStatus !== 'Processed_successful' &&
        invoice.goodXStatus !== 'mfREJ' &&
        invoice.goodXStatus !== 'swREJ' &&
        invoice.goodXStatus !== 'Private'
    )

    if (pendingInvoices.length === 0) {
      message.info('No pending invoices to check')
      return
    }

    const hide = message.loading(`Checking status for ${pendingInvoices.length} invoices...`, 0)
    let successCount = 0
    let errorCount = 0

    for (const invoice of pendingInvoices) {
      try {
        // The goodXStatus function returns the claim status
        let claimStatus = await goodXStatus(invoice.goodXTicketNo)
        let ticketStatus = 'Processing' // Default ticket status

        // Map claim status to ticket status
        if (claimStatus === 'mfAPP' || claimStatus === 'PAID') {
          ticketStatus = 'Processed_successful'
        } else if (claimStatus === 'mfREJ' || claimStatus === 'swREJ') {
          ticketStatus = 'Failed'
        } else if (claimStatus === 'mfACK' || claimStatus === 'swACC' || claimStatus === 'Entry_created') {
          ticketStatus = 'Processing'
        } else if (claimStatus === 'Processed_successful') {
          // Sometimes the API returns the ticket status instead of claim status
          ticketStatus = claimStatus
        } else {
          ticketStatus = 'Pending'
        }

        // Only update if status changed
        if (claimStatus !== invoice.goodXStatus) {
          await updateInvoice(invoice._id, {
            goodXStatus: claimStatus,
            goodXTicketStatus: ticketStatus,
            lastStatusCheck: new Date()
          })

          // Also update the corresponding ledger transaction
          try {
            const { default: Agilite } = await import('agilite')
            const { default: BillingEnums } = await import('../../../../Billing-Module-Enhanced/utils/enums')

            // Create Agilite instance
            const agilite = new Agilite({
              apiServerUrl: process.env.REACT_APP_AGILITE_API_URL,
              apiKey: process.env.REACT_APP_AGILITE_API_KEY
            })

            // Try searching by invoiceNo first, then by invoiceRef as fallback
            let ledgerResponse = await agilite.Connectors.execute(
              BillingEnums.profileKeys.PATIENT_LEDGER,
              BillingEnums.routeKeys.FIND_ONE,
              {
                data: JSON.stringify({
                  invoiceNo: invoice.invoiceNo,
                  type: 'invoice'
                })
              }
            )

            // If not found by invoiceNo, try invoiceRef
            if (!ledgerResponse?.data?._id) {
              ledgerResponse = await agilite.Connectors.execute(
                BillingEnums.profileKeys.PATIENT_LEDGER,
                BillingEnums.routeKeys.FIND_ONE,
                {
                  data: JSON.stringify({
                    invoiceRef: invoice._id,
                    type: 'invoice'
                  })
                }
              )
            }

            if (ledgerResponse?.data?._id) {
              await agilite.Connectors.execute(BillingEnums.profileKeys.PATIENT_LEDGER, BillingEnums.routeKeys.UPDATE, {
                recordId: ledgerResponse.data._id,
                data: JSON.stringify({
                  status: claimStatus,
                  goodXStatus: claimStatus,
                  goodXTicketStatus: ticketStatus,
                  lastStatusCheck: new Date()
                })
              })
            }
          } catch (err) {
            console.error('Failed to update ledger transaction:', err)
          }

          successCount++
        }

        // Small delay to avoid overwhelming the API
        await new Promise((resolve) => setTimeout(resolve, 500))
      } catch (error) {
        console.error(`Failed to check status for invoice ${invoice.invoiceNo}:`, error)
        errorCount++
      }
    }

    hide()

    if (successCount > 0) {
      message.success(`Updated ${successCount} invoice statuses`)
    }
    if (errorCount > 0) {
      message.warning(`Failed to update ${errorCount} invoices`)
    }

    // Refresh the list
    await fetchInvoices()
  }

  return {
    loading,
    data,
    page,
    pageSize,
    total,
    filters,
    setPage,
    setPageSize,
    setFilters,
    fetchInvoices,
    markInvoiceAsComplete,
    handlePrintInvoice,
    printingInvoiceId,
    setPrintingInvoiceId,
    checkInvoiceStatus,
    checkAllPendingStatuses
  }
}
